import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ResourcePlanningComponent } from '../planning/resource-planning.component';

const CYCLE_MS = 30000;

// Automatische Datenaktualisierung alle 60 Minuten.
const REFRESH_MS = 60 * 60 * 1000;

// Tageswechsel jede Minute überprüfen.
const DATE_CHECK_MS = 60 * 1000;
/** Dauer des Aus- bzw. Einblendens (muss zur CSS-Transition passen). */
const FADE_MS = 400;
/** Höchstens so lange auf die Daten der neuen Ansicht warten, bevor eingeblendet wird. */
const MAX_WAIT_MS = 3000;

/** Ziel beim Verlassen des Monitor-Modus. */
const EXIT_ROUTE = '/ressourcenplanung/planung';

/**
 * Monitor-Modus der Ressourcenplanung: Teamplanung im Vollbild, wechselt automatisch
 * zwischen Wochen- und Jahresansicht.
 *
 * Beenden: ESC verarbeitet der Browser selbst (er verlässt den Vollbildmodus, das
 * keydown-Ereignis kommt dabei meist nicht an). Deshalb ist `fullscreenchange` der
 * maßgebliche Auslöser; ESC ohne Vollbild und der Zurück-Button nutzen dieselbe
 * zentrale `exitMonitor()`-Logik.
 */
@Component({
    selector: 'app-resource-monitor',
    templateUrl: './resource-monitor.component.html',
    styleUrls: ['./resource-monitor.component.css']
})
export class ResourceMonitorComponent implements AfterViewInit, OnDestroy {
    @ViewChild('monitorRoot') monitorRoot!: ElementRef<HTMLElement>;
    @ViewChild(ResourcePlanningComponent) planning?: ResourcePlanningComponent;

    view: 'week' | 'year' = 'week';

    // Aktuelle Seite der Wochenansicht (beginnt bei 0)
    weekPage = 0;

    // Anzahl der benötigten Wochenseiten
    weekPageCount = 1;

    // Aktuelle Jahresseite (0 = Jänner–Juni bzw. ganzes Jahr, 1 = Juli–Dezember)
    yearPage = 0;

    // Anzahl der Jahresseiten (1 = ganzes Jahr, 2 = aufgeteilt); meldet die Planungskomponente
    yearPageCount = 1;

    fading = false;
    fullscreenActive = false;
    fullscreenBlocked = false;

    private cycleTimer?: ReturnType<typeof setInterval>;
    private refreshTimer?: ReturnType<typeof setInterval>;
    /** Timer des laufenden Übergangs (Ausblenden, Warten auf Daten). */
    private transitionTimer?: ReturnType<typeof setTimeout>;
    private destroyed = false;
    private exiting = false;

    private dateCheckTimer?: ReturnType<typeof setInterval>;
    private monitorDatum = new Date().toDateString();
    private letzteAktualisierung = Date.now();
    private readonly CHECK_INTERVAL_MS = 60 * 1000;
    /** Der Monitor war bereits im Vollbild – erst dann bedeutet „Vollbild verlassen“ „Monitor beenden“. */
    private wasFullscreen = false;
    private readonly reducedMotion = typeof window !== 'undefined' &&
        !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    constructor(private router: Router) { }

    ngAfterViewInit(): void {
        void this.enterFullscreen();

        this.monitorDatum = new Date().toDateString();

        // Alle 30 Sekunden zur nächsten Seite: Wochenseiten, dann Jahresseite(n), von vorn.
        this.restartCycleTimer();

        // Alle 60 Minuten neue Daten laden.
        this.refreshTimer = setInterval(
            () => this.planning?.refreshMonitorData(),
            REFRESH_MS
        );

        // Jede Minute prüfen, ob ein neuer Tag begonnen hat.
        this.dateCheckTimer = setInterval(
            () => this.pruefeTageswechsel(),
            DATE_CHECK_MS
        );
    }

    private pruefeTageswechsel(): void {
        if (this.destroyed || this.exiting) {
            return;
        }

        const heute = new Date().toDateString();

        if (heute !== this.monitorDatum) {
            this.monitorDatum = heute;

            // Datum und Daten sofort aktualisieren.
            this.planning?.refreshMonitorData();
        }
    }

    async enterFullscreen(): Promise<void> {
        if (this.destroyed || this.exiting || !this.monitorRoot?.nativeElement) return;
        try {
            if (!document.fullscreenElement) {
                await this.monitorRoot.nativeElement.requestFullscreen();
            }
            this.updateFullscreenState();
            this.fullscreenBlocked = !this.fullscreenActive;
        } catch {
            this.fullscreenBlocked = true;
        }
    }



    private restartCycleTimer(): void {
        if (this.cycleTimer) {
            clearInterval(this.cycleTimer);
        }

        this.cycleTimer = setInterval(() => {
            this.transitionView(1);
        }, CYCLE_MS);
    }

    // ---- Ansichtswechsel mit Überblendung ------------------------------------------

    /**
     * Blendet die aktuelle Ansicht aus, wechselt die Ansicht (dieselbe Planungskomponente,
     * kein Neuaufbau) und blendet erst wieder ein, wenn die Daten der neuen Ansicht
     * geladen sind – so ist nie eine leere oder halb aufgebaute Ansicht sichtbar.
     */
    private transitionView(direction: 1 | -1 = 1): void {
        if (this.destroyed || this.exiting) return;

        // Während einer laufenden Überblendung keinen weiteren Wechsel starten
        if (this.fading) return;

        // Reihenfolge der Seiten (Seitenzahlen kommen laufend aus der Planungskomponente):
        // 0 ... weekPages - 1                 = Wochenseiten
        // weekPages ... weekPages + yearPages - 1 = Jahresseiten (ganzes Jahr bzw. Jän–Jun, Jul–Dez)
        const weekPages = Math.max(1, this.weekPageCount);
        const yearPages = Math.max(1, this.yearPageCount);
        const totalViews = weekPages + yearPages;

        const currentIndex =
            this.view === 'year'
                ? weekPages + Math.min(this.yearPage, yearPages - 1)
                : Math.min(this.weekPage, weekPages - 1);

        const nextIndex =
            (currentIndex + direction + totalViews) % totalViews;

        const applyNextView = () => {
            if (nextIndex >= weekPages) {
                this.yearPage = nextIndex - weekPages;
                this.view = 'year';
            } else {
                this.weekPage = nextIndex;
                this.view = 'week';
            }
        };

        if (this.reducedMotion) {
            applyNextView();
            return;
        }

        this.fading = true;

        this.transitionTimer = setTimeout(() => {
            if (this.destroyed || this.exiting) return;

            applyNextView();

            const startedAt = Date.now();

            this.transitionTimer = setTimeout(
                () => this.waitForDataThenShow(startedAt),
                50
            );
        }, FADE_MS);
    }

    private waitForDataThenShow(startedAt: number): void {
        if (this.destroyed || this.exiting) return;
        const loading = !!this.planning?.loading;
        if (loading && Date.now() - startedAt < MAX_WAIT_MS) {
            this.transitionTimer = setTimeout(() => this.waitForDataThenShow(startedAt), 100);
            return;
        }
        // Einen Frame warten, damit die neue Ansicht gezeichnet ist, bevor sie einblendet.
        this.transitionTimer = setTimeout(() => {
            this.fading = false;
            this.transitionTimer = undefined;
        }, 16);
    }

    // ---- Beenden ----------------------------------------------------------------------

    /**
     * Maßgeblich für ESC und das Verlassen des Vollbilds über Browserfunktionen:
     * war der Monitor im Vollbild und ist es nicht mehr, wird der Monitor beendet.
     */
    @HostListener('document:fullscreenchange')
    onFullscreenChange(): void {
        this.updateFullscreenState();
        if (this.fullscreenActive) {
            this.wasFullscreen = true;
            this.fullscreenBlocked = false;
            return;
        }
        if (this.wasFullscreen && !document.fullscreenElement) {
            void this.exitMonitor();
        }
    }

    /** ESC ohne Vollbild (z. B. wenn der Browser den Vollbildmodus blockiert hat). */
    @HostListener('document:keydown.escape', ['$event'])
    onEscape(event: KeyboardEvent): void {
        event.stopPropagation();
        void this.exitMonitor();
    }

    @HostListener('window:keydown', ['$event'])
    onMonitorKeydown(event: KeyboardEvent): void {
        if (this.destroyed || this.exiting) return;

        switch (event.key) {
            case 'ArrowRight':
                event.preventDefault();
                event.stopPropagation();

                this.changeMonitorView(1);
                break;

            case 'ArrowLeft':
                event.preventDefault();
                event.stopPropagation();

                this.changeMonitorView(-1);
                break;

            case 'Enter':
            case ' ':
            case 'Tab':
                event.preventDefault();
                break;
        }
    }
    private changeMonitorView(direction: 1 | -1): void {
        if (this.destroyed || this.exiting) return;

        // Laufende Überblendung abbrechen
        if (this.transitionTimer) {
            clearTimeout(this.transitionTimer);
            this.transitionTimer = undefined;
        }

        // Übergang zurücksetzen
        this.fading = false;

        // Ansicht in gewünschter Richtung wechseln
        this.transitionView(direction);

        // 30-Sekunden-Timer neu starten
        this.restartCycleTimer();
    }

    /**
     * Zentrale Exit-Logik (ESC, Verlassen des Vollbilds, Zurück-Button). Läuft genau
     * einmal: Timer stoppen, Vollbild beenden, zurück zur Teamplanung. Das durch
     * exitFullscreen ausgelöste fullscreenchange wird durch `exiting` abgefangen.
     */
    async exitMonitor(): Promise<void> {
        if (this.exiting || this.destroyed) return;
        this.exiting = true;
        this.stopTimers();
        this.fading = false;

        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            }
        } catch { /* auch ohne erfolgreiches Verlassen des Vollbilds zurücknavigieren */ }

        let navigated = false;
        try {
            navigated = await this.router.navigateByUrl(EXIT_ROUTE);
        } catch {
            navigated = false;
        }
        if (!navigated) {
            // z. B. wenn ein Guard die Planung verweigert: in den Rahmen der Ressourcenplanung.
            try { navigated = await this.router.navigateByUrl('/ressourcenplanung'); } catch { navigated = false; }
        }
        if (!navigated && !this.destroyed) {
            // Navigation nicht möglich: Monitor bleibt bedienbar, erneutes Beenden möglich.
            this.exiting = false;
        }
    }

    private updateFullscreenState(): void {
        this.fullscreenActive = !!this.monitorRoot?.nativeElement &&
            document.fullscreenElement === this.monitorRoot.nativeElement;
    }

    private stopTimers(): void {
        if (this.cycleTimer) clearInterval(this.cycleTimer);
        if (this.refreshTimer) clearInterval(this.refreshTimer);
        if (this.dateCheckTimer) clearInterval(this.dateCheckTimer);
        if (this.transitionTimer) clearTimeout(this.transitionTimer);

        this.cycleTimer = undefined;
        this.refreshTimer = undefined;
        this.dateCheckTimer = undefined;
        this.transitionTimer = undefined;
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.stopTimers();
        if (document.fullscreenElement === this.monitorRoot?.nativeElement) {
            void document.exitFullscreen().catch(() => { });
        }
    }
}
